import { TextField, type TextFieldProps } from '@mui/material';
import { useEffect, useState } from 'react';

/**
 * Reads a number without fighting the person typing it.
 *
 * A plain controlled input that stores the parsed number cannot be typed into: `12.`
 * parses to `12`, which is written straight back into the box and swallows the separator,
 * so a decimal can never be entered. This keeps the raw text while it is being edited and
 * only reports the parsed value upwards.
 *
 * A comma is accepted as the decimal separator — it is what a Turkish keyboard produces,
 * and `Number('12,5')` is `NaN`, which used to blank the field on the keystroke.
 */
const parseNumber = (text: string): number | undefined => {
  const normalized = text.trim().replace(',', '.');

  if (normalized === '') return undefined;

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : undefined;
};

type Props = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value?: number;
  onChange: (value: number | undefined) => void;
  /** When false the separator keys do nothing, so only whole numbers can be entered. */
  allowDecimal?: boolean;
};

const NumberField: React.FC<Props> = ({ value, onChange, allowDecimal = true, ...rest }) => {
  const [text, setText] = useState(value === undefined ? '' : String(value));

  // Follow the model when it changes from somewhere else — a drawer opening on another
  // field, or a type switch — but leave half-typed text alone while it still parses to
  // the value the model already holds.
  useEffect(() => {
    setText((current) => (parseNumber(current) === value ? current : value === undefined ? '' : String(value)));
  }, [value]);

  return (
    <TextField
      {...rest}
      value={text}
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      onChange={(event) => {
        const next = event.target.value;

        // With decimals off the separator is simply not accepted — the keystroke does
        // nothing rather than being silently stripped, which would turn a pasted `12.5`
        // into `125`.
        if (!allowDecimal && /[.,]/.test(next)) return;

        setText(next);
        onChange(parseNumber(next));
      }}
    />
  );
};

export default NumberField;
